import { useEffect, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
    createTaDaClaim,
    updateTaDaClaim,
    getTaDaRules,
    uploadTaDaProof,
} from "services/api/taDaApi";

import {
    showSuccess,
    showError,
} from "utils/toast";


const EMPTY_ITEM = {
    ruleId: "",
    itemDate: "",
    ruleType: "daily_allowance",
    title: "",
    description: "",
    fromLocation: "",
    toLocation: "",
    distanceKm: "",
    claimedAmount: "",
    proofFile: "",
    remarks: "",
};


const INITIAL_FORM = {
    claimDate: "",
    fromDate: "",
    toDate: "",
    title: "",
    description: "",
    claimedAmount: "",
    remarks: "",
    items: [],
};


export default function TaDaClaimModal({
    open,
    onClose,
    reload,
    item,
}) {

    const isEdit =
        Boolean(item);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        loadingRules,
        setLoadingRules,
    ] = useState(false);


    const [
        rules,
        setRules,
    ] = useState([]);


    const [
        form,
        setForm,
    ] = useState(
        INITIAL_FORM
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD RULES
    |--------------------------------------------------------------------------
    */

    const loadRules = async () => {

        try {

            setLoadingRules(true);

            const response =
                await getTaDaRules();


            const data =
                response?.data?.rules || [];


            setRules(
                Array.isArray(data)
                    ? data
                    : []
            );

        }
        catch (error) {

            console.error(
                "TA/DA rules error:",
                error
            );

            setRules([]);

            showError(
                "Unable to load TA/DA rules."
            );

        }
        finally {

            setLoadingRules(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | INITIALIZE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!open) {
            return;
        }


        loadRules();


        if (item) {

            setForm({

                claimDate:
                    item.claimDate
                        ? item.claimDate.substring(
                            0,
                            10
                        )
                        : "",

                fromDate:
                    item.fromDate
                        ? item.fromDate.substring(
                            0,
                            10
                        )
                        : "",

                toDate:
                    item.toDate
                        ? item.toDate.substring(
                            0,
                            10
                        )
                        : "",

                title:
                    item.title || "",

                description:
                    item.description || "",

                claimedAmount:
                    item.claimedAmount
                        ? String(
                            item.claimedAmount
                        )
                        : "",

                remarks:
                    item.remarks || "",

                items:
                    item.items?.map(
                        (claimItem) => ({

                            ruleId:
                                claimItem.ruleId
                                    ? String(
                                        claimItem.ruleId
                                    )
                                    : "",

                            itemDate:
                                claimItem.itemDate
                                    ? claimItem.itemDate.substring(
                                        0,
                                        10
                                    )
                                    : "",

                            ruleType:
                                claimItem.ruleType ||
                                "daily_allowance",

                            title:
                                claimItem.title ||
                                "",

                            description:
                                claimItem.description ||
                                "",

                            fromLocation:
                                claimItem.fromLocation ||
                                "",

                            toLocation:
                                claimItem.toLocation ||
                                "",

                            distanceKm:
                                claimItem.distanceKm
                                    ? String(
                                        claimItem.distanceKm
                                    )
                                    : "",

                            claimedAmount:
                                claimItem.claimedAmount
                                    ? String(
                                        claimItem.claimedAmount
                                    )
                                    : "",

                            proofFile:
                                claimItem.proofFile ||
                                "",

                            remarks:
                                claimItem.remarks ||
                                "",

                        })
                    ) || [],

            });

        }
        else {

            setForm({
                ...INITIAL_FORM,

                claimDate:
                    new Date()
                        .toISOString()
                        .substring(
                            0,
                            10
                        ),

                items: [
                    {
                        ...EMPTY_ITEM,
                    },
                ],
            });

        }

    }, [
        open,
        item,
    ]);


    /*
    |--------------------------------------------------------------------------
    | UPDATE TOP FIELD
    |--------------------------------------------------------------------------
    */

    const updateField = (
        key,
        value
    ) => {

        setForm(
            (prev) => ({
                ...prev,
                [key]: value,
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | UPDATE ITEM
    |--------------------------------------------------------------------------
    */

    const updateItem = (
        index,
        key,
        value
    ) => {

        setForm(
            (prev) => {

                const items =
                    [...prev.items];

                items[index] = {
                    ...items[index],
                    [key]: value,
                };

                return {
                    ...prev,
                    items,
                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | RULE SELECTION
    |--------------------------------------------------------------------------
    */

    const selectRule = (
        index,
        ruleId
    ) => {

        const rule =
            rules.find(
                (r) =>
                    String(r.id) ===
                    String(ruleId)
            );


        setForm(
            (prev) => {

                const items =
                    [...prev.items];


                items[index] = {

                    ...items[index],

                    ruleId,

                    ruleType:
                        rule?.ruleType ||
                        items[index].ruleType,

                };


                return {
                    ...prev,
                    items,
                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | ADD ITEM
    |--------------------------------------------------------------------------
    */

    const addItem = () => {

        setForm(
            (prev) => ({

                ...prev,

                items: [
                    ...prev.items,
                    {
                        ...EMPTY_ITEM,
                        itemDate:
                            prev.claimDate ||
                            "",
                    },
                ],

            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | REMOVE ITEM
    |--------------------------------------------------------------------------
    */

    const removeItem = (
        index
    ) => {

        setForm(
            (prev) => {

                const items =
                    prev.items.filter(
                        (_, i) =>
                            i !== index
                    );


                return {

                    ...prev,

                    items:
                        items.length
                            ? items
                            : [
                                {
                                    ...EMPTY_ITEM,
                                },
                            ],

                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | PROOF UPLOAD
    |--------------------------------------------------------------------------
    */

    const uploadProof = async (
        index,
        file
    ) => {

        if (!file) {
            showError("Please select a file.");
            return;
        }


        console.log(
            "SELECTED TA/DA FILE:",
            file
        );

        console.log(
            "File name:",
            file.name
        );

        console.log(
            "File type:",
            file.type
        );

        console.log(
            "File size:",
            file.size
        );


        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
            "application/pdf",
        ];


        if (!allowedTypes.includes(file.type)) {

            showError(
                "Only JPG, PNG, WEBP or PDF files are allowed."
            );

            return;
        }


        if (file.size > 5 * 1024 * 1024) {

            showError(
                "Proof file must be 5 MB or smaller."
            );

            return;
        }


        try {

            setForm((prev) => {

                const items = [...prev.items];

                items[index] = {
                    ...items[index],
                    uploadingProof: true,
                };

                return {
                    ...prev,
                    items,
                };

            });


            const response =
                await uploadTaDaProof(file);


            console.log(
                "TA/DA UPLOAD RESPONSE:",
                response
            );


            const filePath =
                response?.data?.filePath;


            if (!filePath) {

                throw new Error(
                    "Upload response does not contain filePath."
                );

            }


            setForm((prev) => {

                const items = [...prev.items];

                items[index] = {
                    ...items[index],
                    proofFile: filePath,
                    uploadingProof: false,
                };

                return {
                    ...prev,
                    items,
                };

            });


            showSuccess(
                "Proof uploaded successfully."
            );

        }
        catch (error) {

            console.error(
                "TA/DA PROOF UPLOAD ERROR:",
                error
            );

            console.error(
                "TA/DA PROOF RESPONSE:",
                error?.response?.data
            );


            setForm((prev) => {

                const items = [...prev.items];

                items[index] = {
                    ...items[index],
                    uploadingProof: false,
                    proofFile: "",
                };

                return {
                    ...prev,
                    items,
                };

            });


            showError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to upload proof."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | VALIDATE
    |--------------------------------------------------------------------------
    */

    const validate = () => {

        if (!form.claimDate) {

            showError(
                "Claim date is required."
            );

            return false;

        }


        if (!form.title.trim()) {

            showError(
                "Claim title is required."
            );

            return false;

        }


        if (!form.claimedAmount.trim()) {

            showError(
                "Claimed amount is required."
            );

            return false;

        }


        if (
            !form.items.length
        ) {

            showError(
                "At least one claim item is required."
            );

            return false;

        }


        for (
            let i = 0;
            i < form.items.length;
            i++
        ) {

            const claimItem =
                form.items[i];


            if (!claimItem.itemDate) {

                showError(
                    `Item ${i + 1}: date is required.`
                );

                return false;

            }


            if (!claimItem.ruleType) {

                showError(
                    `Item ${i + 1}: rule type is required.`
                );

                return false;

            }


            if (!claimItem.title.trim()) {

                showError(
                    `Item ${i + 1}: title is required.`
                );

                return false;

            }


            if (
                !claimItem.claimedAmount.trim()
            ) {

                showError(
                    `Item ${i + 1}: claimed amount is required.`
                );

                return false;

            }

        }


        return true;

    };


    /*
    |--------------------------------------------------------------------------
    | SAVE
    |--------------------------------------------------------------------------
    */

    const submit = async (
        e
    ) => {

        e.preventDefault();


        if (!validate()) {
            return;
        }


        try {

            setLoading(true);


            const payload = {

                claimDate:
                    form.claimDate,

                fromDate:
                    form.fromDate ||
                    undefined,

                toDate:
                    form.toDate ||
                    undefined,

                title:
                    form.title.trim(),

                description:
                    form.description.trim() ||
                    undefined,

                claimedAmount:
                    form.claimedAmount.trim(),

                remarks:
                    form.remarks.trim() ||
                    undefined,

                items:
                    form.items.map(
                        (claimItem) => ({

                            ruleId:
                                claimItem.ruleId ||
                                undefined,

                            itemDate:
                                claimItem.itemDate,

                            ruleType:
                                claimItem.ruleType,

                            title:
                                claimItem.title.trim(),

                            description:
                                claimItem.description.trim() ||
                                undefined,

                            fromLocation:
                                claimItem.fromLocation.trim() ||
                                undefined,

                            toLocation:
                                claimItem.toLocation.trim() ||
                                undefined,

                            distanceKm:
                                claimItem.distanceKm.trim() ||
                                undefined,

                            claimedAmount:
                                claimItem.claimedAmount.trim(),

                            proofFile:
                                claimItem.proofFile ||
                                undefined,

                            remarks:
                                claimItem.remarks.trim() ||
                                undefined,

                        })
                    ),

            };


            if (isEdit) {

                await updateTaDaClaim(
                    item.id,
                    payload
                );


                showSuccess(
                    "TA/DA claim updated successfully."
                );

            }
            else {

                await createTaDaClaim(
                    payload
                );


                showSuccess(
                    "TA/DA claim created successfully."
                );

            }


            await reload();

            onClose();

        }
        catch (error) {

            console.error(
                "TA/DA save error:",
                error
            );


            showError(
                error?.response?.data?.message ||
                "Unable to save TA/DA claim."
            );

        }
        finally {

            setLoading(false);

        }

    };


    if (!open) {
        return null;
    }


    return (

        <CrudModal

            open={open}

            onClose={onClose}

            onSubmit={submit}

            loading={loading}

            title={
                isEdit
                    ? "Edit TA/DA Claim"
                    : "Create TA/DA Claim"
            }

            submitText={
                isEdit
                    ? "Update"
                    : "Save"
            }

            width="max-w-6xl"

        >

            {/* ========================================================
                HEADER
            ========================================================= */}

            <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">


                <CrudInput
                    label="Claim Date"
                    type="date"
                    value={
                        form.claimDate
                    }
                    onChange={(e) =>
                        updateField(
                            "claimDate",
                            e.target.value
                        )
                    }
                />


                <CrudInput
                    label="Title"
                    value={
                        form.title
                    }
                    onChange={(e) =>
                        updateField(
                            "title",
                            e.target.value
                        )
                    }
                />


                <CrudInput
                    label="From Date"
                    type="date"
                    value={
                        form.fromDate
                    }
                    onChange={(e) =>
                        updateField(
                            "fromDate",
                            e.target.value
                        )
                    }
                />


                <CrudInput
                    label="To Date"
                    type="date"
                    value={
                        form.toDate
                    }
                    onChange={(e) =>
                        updateField(
                            "toDate",
                            e.target.value
                        )
                    }
                />


                <CrudInput
                    label="Claimed Amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={
                        form.claimedAmount
                    }
                    onChange={(e) =>
                        updateField(
                            "claimedAmount",
                            e.target.value
                        )
                    }
                />


                <CrudInput
                    label="Description"
                    value={
                        form.description
                    }
                    onChange={(e) =>
                        updateField(
                            "description",
                            e.target.value
                        )
                    }
                />

            </div>


            {/* ========================================================
                ITEMS
            ========================================================= */}

            <div className="mb-4 flex items-center justify-between">

                <h3
                    className="
                        text-lg
                        font-bold
                        text-navy-700
                        dark:text-white
                    "
                >
                    Claim Items
                </h3>


                <button
                    type="button"
                    onClick={addItem}
                    className="
                        rounded-lg
                        bg-blue-600
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        text-white
                        hover:bg-blue-700
                    "
                >
                    + Add Item
                </button>

            </div>


            {form.items.map(
                (claimItem, index) => (

                    <div
                        key={index}
                        className="
                            mb-5
                            rounded-xl
                            border
                            border-gray-200
                            bg-gray-50
                            p-4
                        "
                    >

                        <div
                            className="
                                mb-4
                                flex
                                items-center
                                justify-between
                            "
                        >

                            <h4
                                className="
                                    font-semibold
                                    text-gray-800
                                "
                            >
                                Item #{index + 1}
                            </h4>


                            <button
                                type="button"
                                onClick={() =>
                                    removeItem(index)
                                }
                                className="
                                    rounded-lg
                                    bg-red-600
                                    px-3
                                    py-1.5
                                    text-xs
                                    font-semibold
                                    text-white
                                    hover:bg-red-700
                                "
                            >
                                Remove
                            </button>

                        </div>


                        <div
                            className="
                                grid
                                grid-cols-1
                                gap-4
                                md:grid-cols-2
                                lg:grid-cols-3
                            "
                        >


                            <CrudInput
                                label="Item Date"
                                type="date"
                                value={
                                    claimItem.itemDate
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "itemDate",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudSelect
                                label={
                                    loadingRules
                                        ? "Rule (Loading...)"
                                        : "TA/DA Rule"
                                }
                                value={
                                    claimItem.ruleId
                                }
                                onChange={(e) =>
                                    selectRule(
                                        index,
                                        e.target.value
                                    )
                                }
                                options={[
                                    {
                                        value: "",
                                        label: "Select Rule",
                                    },

                                    ...rules.map(
                                        (rule) => ({

                                            value:
                                                String(
                                                    rule.id
                                                ),

                                            label:
                                                `${rule.name} - ₹${rule.amount}`,

                                        })
                                    ),
                                ]}
                            />


                            <CrudSelect
                                label="Rule Type"
                                value={
                                    claimItem.ruleType
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "ruleType",
                                        e.target.value
                                    )
                                }
                                options={[
                                    {
                                        value: "daily_allowance",
                                        label: "Daily Allowance",
                                    },
                                    {
                                        value: "travel_allowance",
                                        label: "Travel Allowance",
                                    },
                                    {
                                        value: "stay_allowance",
                                        label: "Stay Allowance",
                                    },
                                    {
                                        value: "food_allowance",
                                        label: "Food Allowance",
                                    },
                                    {
                                        value: "other",
                                        label: "Other",
                                    },
                                ]}
                            />


                            <CrudInput
                                label="Title"
                                value={
                                    claimItem.title
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "title",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="From Location"
                                value={
                                    claimItem.fromLocation
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "fromLocation",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="To Location"
                                value={
                                    claimItem.toLocation
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "toLocation",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="Distance (KM)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={
                                    claimItem.distanceKm
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "distanceKm",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="Claimed Amount"
                                type="number"
                                step="0.01"
                                min="0"
                                value={
                                    claimItem.claimedAmount
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "claimedAmount",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="Description"
                                value={
                                    claimItem.description
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "description",
                                        e.target.value
                                    )
                                }
                            />


                            <CrudInput
                                label="Remarks"
                                value={
                                    claimItem.remarks
                                }
                                onChange={(e) =>
                                    updateItem(
                                        index,
                                        "remarks",
                                        e.target.value
                                    )
                                }
                            />


                            {/* PROOF */}

                            <div className="md:col-span-2 lg:col-span-3">

    <label
        className="
            mb-2
            block
            text-sm
            font-medium
            text-gray-700
        "
    >
        Proof / Ticket / Bill
    </label>


   <input
    type="file"
    accept=".jpg,.jpeg,.png,.webp,.pdf"
    disabled={
        claimItem.uploadingProof ||
        loading
    }
    onChange={(event) => {

        const file =
            event.currentTarget.files?.[0];

        console.log(
            "INPUT FILE:",
            file
        );

        uploadProof(
            index,
            file
        );

    }}
    className="
        block
        w-full
        rounded-lg
        border
        border-gray-300
        bg-white
        p-2
        text-sm
    "
/>


    {claimItem.uploadingProof && (

        <div
            className="
                mt-2
                text-sm
                text-blue-600
            "
        >
            Uploading proof...
        </div>

    )}


    {claimItem.proofFile &&
        !claimItem.uploadingProof && (

        <div
            className="
                mt-2
                rounded-lg
                border
                border-green-200
                bg-green-50
                p-3
            "
        >

            <div
                className="
                    text-sm
                    font-semibold
                    text-green-700
                "
            >
                ✓ Proof uploaded
            </div>


            <div
                className="
                    mt-1
                    break-all
                    text-xs
                    text-gray-600
                "
            >
                {claimItem.proofFile}
            </div>


            <a
                href={
                    `/uploads/${claimItem.proofFile}`
                }
                target="_blank"
                rel="noreferrer"
                className="
                    mt-2
                    inline-block
                    rounded-lg
                    bg-blue-600
                    px-3
                    py-1.5
                    text-xs
                    font-semibold
                    text-white
                    hover:bg-blue-700
                "
            >
                View Proof
            </a>

        </div>

    )}

</div>


                        </div>

                    </div>

                )
            )}


            {/* ========================================================
                REMARKS
            ========================================================= */}

            <CrudInput
                label="Remarks"
                value={
                    form.remarks
                }
                onChange={(e) =>
                    updateField(
                        "remarks",
                        e.target.value
                    )
                }
            />

        </CrudModal>
    );
}